if (!customElements.get('bp-image-magnifier')) {
  customElements.define('bp-image-magnifier', class extends HTMLElement {
    connectedCallback() {
      this.photo = this.querySelector('.bp-inspect-image img');
      this.stage = this.querySelector('.bp-inspect-stage');
      this.surface = this.querySelector('[data-bp-inspect-surface]');
      this.lens = this.querySelector('[data-bp-lens]');
      this.stopButton = this.querySelector('[data-bp-stop]');
      this.fullButton = this.querySelector('[data-bp-full-image]');
      this.status = this.querySelector('[data-bp-inspect-status]');
      if (!this.photo || !this.surface) return;
      this.events = new AbortController();
      const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: this.events.signal });
      this.surface.hidden = false;
      this.querySelector('[data-bp-inspect-help]').hidden = false;
      this.point = { x: .5, y: .5 };
      this.drag = null;
      this.dragFrame = null;
      this.suppressClick = false;
      on(this.surface, 'pointerenter', (event) => {
        if (event.pointerType === 'mouse' && matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.moveTo(event.clientX, event.clientY);
          this.start(false);
        }
      });
      on(this.surface, 'pointermove', (event) => {
        if (event.pointerType === 'mouse' && this.active) this.moveTo(event.clientX, event.clientY);
        else this.moveDrag(event);
      });
      on(this.surface, 'pointerleave', () => { if (!this.pinned) this.stop(); });
      on(this.surface, 'pointerdown', (event) => {
        this.lastPointer = event.pointerType;
        if ((this.drag && event.pointerId !== this.drag.id) || (event.pointerType === 'touch' && event.isPrimary === false)) {
          this.suppressClick = true;
          this.stop();
          return;
        }
        this.suppressClick = false;
        // A first tap only activates zoom. Inactive swipes remain native page gestures.
        if (!this.active || !this.pinned || !['touch', 'pen'].includes(event.pointerType)) return;
        this.drag = { id: event.pointerId, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, moved: false };
        try { this.surface.setPointerCapture(event.pointerId); }
        catch { this.stop(); return; }
        this.moveTo(event.clientX, event.clientY);
        event.preventDefault();
      });
      on(this.surface, 'pointerup', (event) => this.endDrag(event));
      on(this.surface, 'pointercancel', (event) => {
        if (event.pointerType !== 'mouse') this.suppressClick = true;
        this.endDrag(event, true);
      });
      on(this.surface, 'lostpointercapture', (event) => this.endDrag(event, true));
      on(this.surface, 'click', (event) => {
        if (this.suppressClick && event.detail !== 0) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (event.detail === 0) {
          if (this.pinned) this.stop();
          else { this.point = { x: .5, y: .5 }; this.start(true); }
        } else if ((event.pointerType || this.lastPointer) === 'mouse' && matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.fullButton.click();
        } else {
          this.moveTo(event.clientX, event.clientY);
          this.start(true);
        }
      });
      on(this.surface, 'keydown', (event) => {
        const offsets = { ArrowLeft: [-.05, 0], ArrowRight: [.05, 0], ArrowUp: [0, -.05], ArrowDown: [0, .05] };
        if (this.active && offsets[event.key]) {
          event.preventDefault();
          this.pinned = true;
          this.setAttribute('data-pinned', '');
          this.point.x = Math.min(1, Math.max(0, this.point.x + offsets[event.key][0]));
          this.point.y = Math.min(1, Math.max(0, this.point.y + offsets[event.key][1]));
          this.paint();
        }
      });
      on(document, 'keydown', (event) => {
        if (event.key === 'Escape' && this.active) {
          const returnFocus = this.contains(document.activeElement);
          this.stop();
          if (returnFocus) this.surface.focus({ preventScroll: true });
        }
      });
      on(document, 'pointerdown', (event) => {
        if (this.drag && event.pointerId !== this.drag.id && event.pointerType !== 'mouse') {
          this.suppressClick = true;
          this.stop();
        } else if (!this.contains(event.target)) this.stop();
      });
      on(this.stopButton, 'click', () => { this.stop(); this.surface.focus({ preventScroll: true }); });
      on(this.fullButton, 'click', () => this.stop());
      on(this, 'focusout', (event) => { if (event.relatedTarget && !this.contains(event.relatedTarget)) this.stop(); });
      on(window, 'blur', () => this.stop());
      on(document, 'visibilitychange', () => { if (document.hidden) this.stop(); });
      on(this.closest('media-gallery'), 'slideChanged', () => this.stop());
      this.stageSize = this.stage.getBoundingClientRect();
      this.resizeObserver = new ResizeObserver(() => {
        const size = this.stage.getBoundingClientRect();
        if (this.stageSize.width > 0 && this.stageSize.height > 0 && (Math.abs(size.width - this.stageSize.width) > .5 || Math.abs(size.height - this.stageSize.height) > .5)) this.stop();
        this.stageSize = size;
      });
      this.resizeObserver.observe(this.stage);
      this.slideObserver = new MutationObserver(() => this.stop());
      const slide = this.closest('.product__media-item');
      if (slide) this.slideObserver.observe(slide, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    }

    moveDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.id || !this.active) return;
      this.drag.x = event.clientX;
      this.drag.y = event.clientY;
      this.drag.moved ||= Math.hypot(event.clientX - this.drag.startX, event.clientY - this.drag.startY) > 6;
      event.preventDefault();
      if (this.dragFrame !== null) return;
      this.dragFrame = requestAnimationFrame(() => {
        this.dragFrame = null;
        if (this.drag && this.active) this.moveTo(this.drag.x, this.drag.y);
      });
    }

    releaseDrag(suppressClick = false) {
      if (this.dragFrame !== null) cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;
      const drag = this.drag;
      this.drag = null;
      if (suppressClick) this.suppressClick = true;
      if (drag) {
        try { if (this.surface.hasPointerCapture?.(drag.id)) this.surface.releasePointerCapture(drag.id); }
        catch { /* The browser may already have released a cancelled or detached pointer. */ }
      }
    }

    endDrag(event, cancelled = false) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const moved = this.drag.moved || Math.hypot(event.clientX - this.drag.startX, event.clientY - this.drag.startY) > 6;
      if (!cancelled) this.moveTo(event.clientX, event.clientY);
      this.releaseDrag(cancelled || moved);
      if (cancelled) this.stop();
    }

    imageBounds() {
      const box = this.photo.getBoundingClientRect();
      const ratio = this.photo.naturalWidth / this.photo.naturalHeight;
      if (!ratio || !box.width || !box.height) return null;
      const width = Math.min(box.width, box.height * ratio);
      const height = width / ratio;
      return { left: box.left + (box.width - width) / 2, top: box.top + (box.height - height) / 2, width, height };
    }

    moveTo(x, y) {
      const bounds = this.imageBounds();
      if (!bounds) return;
      this.point = { x: Math.min(1, Math.max(0, (x - bounds.left) / bounds.width)), y: Math.min(1, Math.max(0, (y - bounds.top) / bounds.height)) };
      this.paint();
    }

    start(pinned) {
      if (!this.imageBounds()) return;
      this.status.textContent = '';
      this.active = true;
      this.pinned = pinned || this.pinned;
      this.toggleAttribute('data-pinned', Boolean(this.pinned));
      this.surface.setAttribute('aria-pressed', 'true');
      this.stopButton.hidden = false;
      if (this.loaded) return this.paint();
      if (this.loading) return;
      this.loading = true;
      this.setAttribute('data-loading', '');
      this.source = new Image();
      this.source.onload = () => {
        this.loaded = true;
        this.loading = false;
        this.removeAttribute('data-loading');
        this.lens.style.backgroundImage = `url(${JSON.stringify(this.dataset.imageUrl)})`;
        if (this.isConnected && this.active) this.paint();
      };
      this.source.onerror = () => {
        this.loading = false;
        this.removeAttribute('data-loading');
        this.stop();
        this.status.textContent = this.dataset.errorLabel;
      };
      this.source.src = this.dataset.imageUrl;
    }

    paint() {
      if (!this.active || !this.loaded) return;
      const bounds = this.imageBounds();
      if (!bounds) return this.stop();
      const stage = this.stage.getBoundingClientRect();
      const diameter = Math.min(220, bounds.width, bounds.height, Math.max(140, bounds.width * .45));
      const centerX = Math.max(diameter / 2, Math.min(bounds.width - diameter / 2, this.point.x * bounds.width));
      const centerY = Math.max(diameter / 2, Math.min(bounds.height - diameter / 2, this.point.y * bounds.height));
      const zoom = 2.5;
      Object.assign(this.lens.style, {
        width: `${diameter}px`, height: `${diameter}px`,
        left: `${bounds.left - stage.left + centerX - diameter / 2}px`,
        top: `${bounds.top - stage.top + centerY - diameter / 2}px`,
        backgroundSize: `${bounds.width * zoom}px ${bounds.height * zoom}px`,
        backgroundPosition: `${diameter / 2 - this.point.x * bounds.width * zoom}px ${diameter / 2 - this.point.y * bounds.height * zoom}px`
      });
      this.lens.hidden = false;
    }

    stop() {
      this.releaseDrag(Boolean(this.drag));
      this.active = false;
      this.pinned = false;
      this.removeAttribute('data-pinned');
      if (this.lens) this.lens.hidden = true;
      if (this.stopButton) this.stopButton.hidden = true;
      this.surface?.setAttribute('aria-pressed', 'false');
    }

    disconnectedCallback() {
      this.stop();
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.slideObserver?.disconnect();
      if (this.source) this.source.onload = this.source.onerror = null;
      this.loading = false;
      this.removeAttribute('data-loading');
    }
  });
}
